import { buildFetchConfig } from '@/utils/fetchUtils';
import type {
  ContextItem,
  ContextNoteCreateRequest,
  ContextNoteUpdateRequest,
  ContextUploadOptions,
  ListContextResponse,
  TenantContextItem,
  UserContextItem,
} from '@Types/knowledge';

/** Must match w3f-agent-server `context_document` limits. */
export const CONTEXT_RAG_THRESHOLD_KB = 100;
export const CONTEXT_MAX_UPLOAD_MB = 6.6;
export const CONTEXT_MAX_UPLOAD_BYTES = Math.floor(
  CONTEXT_MAX_UPLOAD_MB * 1024 * 1024,
);

class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message || `Request failed: ${status}`);
    this.status = status;
    this.body = body;
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const { baseURL, headers } = buildFetchConfig();
  const response = await fetch(`${baseURL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // non-JSON error body
    }
    throw new HttpError(
      response.status,
      body,
      formatHttpErrorMessage(response.status, body),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
};

export const listContext = (): Promise<ListContextResponse> =>
  request<ListContextResponse>('/context');

export const getContextNote = (contextId: string): Promise<ContextItem> =>
  request<ContextItem>(`/context/${contextId}`);

export const createContextNote = (
  body: ContextNoteCreateRequest,
): Promise<ContextItem> =>
  request<ContextItem>('/context', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateContextNote = (
  contextId: string,
  body: ContextNoteUpdateRequest,
): Promise<ContextItem> =>
  request<ContextItem>(`/context/${contextId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

const headersForFormData = (headers: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(headers).filter(
      ([key]) => key.toLowerCase() !== 'content-type',
    ),
  );

const formatHttpErrorMessage = (status: number, body: unknown): string => {
  if (body && typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map(item => {
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: unknown }).msg);
          }
          return JSON.stringify(item);
        })
        .join('; ');
    }
  }
  return `Request failed: ${status}`;
};

export const uploadContextDocument = async ({
  file,
  scope = 'user',
  sourcePrompt,
}: ContextUploadOptions): Promise<ContextItem> => {
  const { baseURL, headers } = buildFetchConfig();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scope', scope);
  if (sourcePrompt?.trim()) {
    formData.append('source_prompt', sourcePrompt.trim());
  }

  const response = await fetch(`${baseURL}/context/upload`, {
    method: 'POST',
    headers: headersForFormData(headers),
    body: formData,
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // ignore
    }
    throw new HttpError(
      response.status,
      body,
      formatHttpErrorMessage(response.status, body),
    );
  }

  return response.json();
};

export const deleteContextNote = (contextId: string): Promise<void> =>
  request<void>(`/context/${contextId}`, { method: 'DELETE' });

export { HttpError };
